import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { finalize, takeUntil } from 'rxjs/operators';
import { RestService, AccountEmailMap, IS_NOT_NULL_OR_UNDEFINED } from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';

import { BaseComponent } from '@shared/base/base.component';
import { AccountEmailMapDialogComponent } from '../account-email-map-dialog/account-email-map-dialog.component';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';

@Component({
	selector: 'app-account-email-map-table',
	templateUrl: './account-email-map-table.component.html',
	styleUrls: ['./account-email-map-table.component.scss'],
})
export class AccountEmailMapTableComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() accountId!: number;

	// data table variables
	isLoadingList: boolean = false;
	displayedColumns = [
		'firstName',
		'lastName',
		'email',
		'state',
		'createdAt',
		'updatedAt',
		'actions',
	];
	@ViewChild(MatPaginator) paginator: MatPaginator;
	dataSource = new MatTableDataSource<AccountEmailMap>([]);
	totalRecords = 0;
	PAGE_SIZE: number = 25;

	constructor(
		private restService: RestService,
		private urlService: UrlService,
		private utilService: UtilService,
		public dialog: MatDialog
	) {
		super();
	}

	ngOnInit(): void {
		this.initializePage();
		this.getAccountEmailMaps();
	}

	initializePage(): void {
		this.dataSource.paginator = this.paginator;
	}

	getAccountEmailMaps() {
		this.isLoadingList = true;
		this.dataSource = new MatTableDataSource<AccountEmailMap>([]);
		const url = this.urlService.getAccountEmailMap(
			this.accountId,
			this.paginator?.pageIndex || 0,
			this.PAGE_SIZE
		);
		this.restService
			.GET(url)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isLoadingList = false;
				})
			)
			.subscribe(
				(response: any) => {
					this.totalRecords = response.data?.totalElements;
					this.dataSource = new MatTableDataSource(response.data?.content);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Cannot fetch account emails.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	addEmail(): void {
		const dialogRef = this.dialog.open(AccountEmailMapDialogComponent, {
			width: '600px',
			data: {
				title: 'Add New Email',
				cancelButtonText: 'Cancel',
				ConfirmButtonText: 'Add',
				addEmail: true,
				accountId: this.accountId,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (IS_NOT_NULL_OR_UNDEFINED(result)) {
				this.getAccountEmailMaps();
			}
		});
	}

	deleteAccountEmail(accountEmailMap: AccountEmailMap): void {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				title: 'Please confirm',
				body: `Are you sure you want to delete email ${accountEmailMap?.c4UserDTO?.email} ?`,
				cancelButtonText: 'No',
				ConfirmButtonTest: 'Yes',
			},
			disableClose: true,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.isLoadingList = true;
				const url = this.urlService.deleteAccountEmailMap(accountEmailMap.id);
				this.restService
					.DELETE(url)
					.pipe(
						takeUntil(this.isDestroyed$),
						finalize(() => {
							this.isLoadingList = false;
						})
					)
					.subscribe(
						(response: any) => {
							this.utilService.showSnackbar('Email deleted', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
							this.dataSource.data = this.dataSource.data.filter(
								(accountEmail) =>
									accountEmail?.c4UserDTO?.email !== accountEmailMap?.c4UserDTO?.email
							);
							this.dataSource = new MatTableDataSource<any>(this.dataSource.data);
						},
						(error) => {
							this.utilService.showSnackbar('Cannot delete email', SNACKBAR.ERROR, SNACKBAR.ACTION);
						}
					);
			}
		});
	}

	editAccountEmail(accountEmailMap: AccountEmailMap): void {
		const dialogRef = this.dialog.open(AccountEmailMapDialogComponent, {
			width: '600px',
			data: {
				title: 'Edit Email',
				cancelButtonText: 'Cancel',
				ConfirmButtonText: 'Update',
				editEmail: true,
				accountId: this.accountId,
				accountEmailMap,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (IS_NOT_NULL_OR_UNDEFINED(result)) {
				this.dataSource.data = this.dataSource.data.map((accountEmail) => {
					if (accountEmail.id === accountEmailMap.id) {
						return result;
					}
					return accountEmail;
				});
				this.dataSource = new MatTableDataSource<any>(this.dataSource.data);
			}
		});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
